import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Filter state that lives in the URL query, so a link can open a page already
 * filtered (the dashboard's "Pending by vendor" bars open `…/review?vendor=fre0`)
 * and the filter survives a reload.
 *
 * `defaults` declares the keys and their types: an array default is a
 * multi-value param (`?vendor=pet0&vendor=petv`), a string default a single
 * one. A value equal to its default is left out of the URL. Writes use
 * `replace`, so changing filters doesn't stack history entries. Params not in
 * `defaults` are left alone.
 */
export type UrlFilterSpec = Record<string, string | string[]>;

export function useUrlFilters<T extends UrlFilterSpec>(
  defaults: T,
): [T, (patch: Partial<T>) => void, () => void] {
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  // Keyed by the serialised defaults so callers can pass an object literal.
  const defaultsKey = JSON.stringify(defaults);

  // Stable identity per query string: pages feed these into effect deps.
  const values = useMemo(() => {
    const current = new URLSearchParams(query);
    const d = JSON.parse(defaultsKey) as T;
    const out: UrlFilterSpec = {};
    for (const [key, def] of Object.entries(d)) {
      if (Array.isArray(def)) {
        const all = current.getAll(key).filter(Boolean);
        out[key] = all.length ? all : def;
      } else {
        out[key] = current.get(key) ?? def;
      }
    }
    return out as T;
  }, [query, defaultsKey]);

  const update = useCallback(
    (patch: Partial<T>) => {
      const d = JSON.parse(defaultsKey) as T;
      setParams(
        prev => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (!(key in d) || value === undefined) continue;
            next.delete(key);
            if (Array.isArray(value)) {
              for (const v of value) if (v) next.append(key, v);
            } else if (value !== d[key]) {
              next.set(key, value as string);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [defaultsKey, setParams],
  );

  const reset = useCallback(() => {
    const d = JSON.parse(defaultsKey) as T;
    setParams(
      prev => {
        const next = new URLSearchParams(prev);
        for (const key of Object.keys(d)) next.delete(key);
        return next;
      },
      { replace: true },
    );
  }, [defaultsKey, setParams]);

  return [values, update, reset];
}
